<template>
  <div>
    <v-skeleton-loader
      v-if="isInitializing"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <TableRemote
      v-else
      :pagination="pagination"
      :iconHeader="hasPermission('analysisRequests.add') && hasPermission('nodes.tree.view') ? 'add' : null"
      :loading="loading"
      :items="displayedAnalysisRequestTemplates"
      :isSearchable="false"
      :headers="templateHeaders"
      :sortableColumns="sortableColumns"
      :isInfinitScroll="true"
      :disablePagination="true"
      hideDefaultFooter
      :centerColumns="['billable', 'analysisAction']"
      :notSortableColumns="['billable']"
      :title="$t('analysisRequests.templatePageName')"
      @sendQueryParams="fetchQuery"
      @iconAction="onClickAction"
    >
      <template #SearchWithFilters>
        <SearchWithFilters
          :searchPlaceholder="$t('analysisRequests.labels.template.searchBy')"
          :filterName="filterName"
          @onSearch="handleSearch"
        />
      </template>
      <template v-slot:[`item.billable`]="{item}">
        <VCheckbox
          v-model="item.billable"
          dense
          color="primary"
          class="mt-0 d-flex justify-center"
        />
      </template>
      <template #item.analysisAction="{item}">
        <VTableAction
          class="action-buttons"
          :itemActions="actions"
          :isCustomIcon="true"
          :verticalActionDots="true"
          @iconAction="(action) => onClickAction(item, action)"
        />
      </template>
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
    </TableRemote>
    <AnalysisRequestModal
      v-if="isVisible"
      :isVisible="isVisible"
      :title="$t('analysisRequests.labels.template.addNewTemplate')"
      :sample="sample"
      :isEdition="isEdition"
      @hide="isVisible = false"
      @confirm="onConfirm($event)"
    />
    <Modal
      v-if="showModal"
      :params="modalParams"
    />
  </div>
</template>

<script>
import Vue from 'vue';
import { cloneDeep } from 'lodash';
import { mapGetters } from 'vuex';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm.vue';
import Modal from '@/components/shared/forms-controls/Modal.vue';
import VCheckbox from '@/components/shared/widgets/forms/VuetifyInputs/VCheckbox.vue';
import perpage from '@/constants/pagination';
import VTableAction from '@/components/shared/widgets/VTableAction.vue';
import AnalysisRequestModal from './modal.vue';

export default {
    components: {
        VTableAction,
        TableRemote,
        SearchWithFilters,
        AnalysisRequestModal,
        Modal,
        VCheckbox,
    },
    data() {
        return {
            isInitializing: false,
            loading: false,
            templates: [],
            pagination: {},
            queryParams: { perpage, page: 1 },
            isVisible: false,
            showModal: false,
            modalParams: {},
            sample: {},
            filterName: 'templateAnalysisRequest',
            isEdition: false,
            sortableColumns: ['name', 'component', 'sampleType', 'createdBy'],
        };
    },
    computed: {
        ...mapGetters([
            'currentMember',
            'hasPermission',
            'hasRole',
            'userLang',
            'site',
            'client',
        ]),
        isSite() {
            return !!this.siteId?.length;
        },

        templateHeaders() {
            const headerArr = [
                'name',
                'component',
                'sampleType',
                'createdBy',
                'billable',
            ].map(value => ({
                text: this.$t(`analysisRequests.labels.template.${value}`),
                value,
                sortable: this.sortableColumns.includes(value),
            }));
            headerArr.push({
                sortable: false,
                text: this.$t('controls.actions'),
                value: 'analysisAction',
            });
            return headerArr;
        },
        displayedAnalysisRequestTemplates() {
            return this.templates.map(template => ({
                ...template,
                createdBy: template.requestedBy && template.requestedBy.user
                    ? `${template.requestedBy.user.firstName} ${template.requestedBy.user.lastName}`
                    : '',
                component: template.component?.id ? template.component.name : '',
                componentId: template.component?.id,
                sampleType: template.samples?.length ? this.$t(`analysisRequests.labels.template.sampleTypes.${template.samples[0].type.toLowerCase()}`) : '',
            }));
        },
        actions() {
            const actions = [];

            actions.push({
                name: this.$t('analysisRequests.labels.template.edit'),
                icon: 'edit',
                iconAndTextColor: '#26384B',
            });
            actions.push({
                name: this.$t('analysisRequests.labels.template.delete'),
                icon: 'delete',
                iconAndTextColor: '#EF4135',
            });
            return actions;
        },
    },
    async created() {
        this.isInitializing = true;
        this.$store.dispatch('setTabs', []);
        await this.getARTemplates();
        this.isInitializing = false;
    },
    methods: {
        async fetchQuery(queryParams) {
            this.queryParams = {
                ...this.queryParams,
                ...queryParams,
                order: this.preparOrderQueryParams(queryParams.order),
            };
            await this.getARTemplates();
        },
        preparOrderQueryParams(value) {
            if (typeof value !== 'string') {
                return null;
            }

            const [name, order] = value.split('_');
            let newValue;
            switch (name) {
                case 'sampleType':
                    newValue = 'type';
                    break;
                case 'name':
                    newValue = 'name';
                    break;
                case 'component':
                    newValue = 'component.name';
                    break;
                case 'createdBy':
                    newValue = 'requestedBy.user.profile.firstName';
                    break;
                default:
                    return null;
            }

            return `${newValue}_${order}`;
        },
        async getARTemplates() {
            this.loading = true;
            if (this.queryParams.page === 1 || this.queryParams?.page <= this.pagination?.totalPageCount) {
                const { data } = await this.$rq.getAnalysisRequestsTemplates(this.site.id, this.queryParams);
                this.templates = [...this.templates, ...data.templates];
                this.pagination = data.pagination;
            }
            this.loading = false;
        },
        async handleSearch(search) {
            this.queryParams = {
                ...this.queryParams,
                search,
                page: 1,
            };
            this.templates = [];
            this.pagination = { perpage };
            await this.getARTemplates();
        },
        async onConfirm(payload) {
            const requestPayload = this.preparePayload(payload);
            requestPayload.requestedBy = this.currentMember.id;
            if (!this.isEdition) {
                const { data } = await this.$rq.addAnalysisRequestTemplate(this.site.id, requestPayload);
                if (data) {
                    this.$toasted.global.toast_success({
                        message: this.$t('analysisRequests.templateAdded'),
                    });
                    await this.getARTemplates();
                }
            } else {
                const content = {
                    ...requestPayload,
                    selectedAnalysisGroups: requestPayload.selectedAnalysisGroups?.map(group => {
                        if (typeof group === 'string') {
                            return group;
                        }
                        return group.id;
                    }),
                    samples: requestPayload.samples.map(sample => ({
                        ...sample,
                        collectedBy: sample.id,
                    })),
                };
                const { data } = await this.$rq.editAnalysisRequestTemplate(this.site.id, requestPayload.id, content);
                if (data) {
                    this.$toasted.global.toast_success({
                        message: this.$t('analysisRequests.templateEdited'),
                    });
                    await this.getARTemplates();
                }
            }
            this.isVisible = false;
        },
        preparePayload(payload) {
            const requestPayload = cloneDeep(payload);
            Object.keys(requestPayload).forEach(key => {
                if (requestPayload[key] === undefined || requestPayload[key] === null) {
                    delete requestPayload[key];
                } else if (requestPayload[key].id) {
                    requestPayload[key] = requestPayload[key].id;
                }
            });

            requestPayload.billable = payload.billable;
            requestPayload.poNumber = payload.poNumber;
            return requestPayload;
        },
        async deleteTemplate(item) {
            this.loading = true;
            const { data } = await this.$rq.deleteAnalysisRequestsTemplates(this.site.id, item.id);
            if (data) {
                this.$toasted.global.toast_success({
                    message: this.$t('analysisRequests.labels.template.deleted'),
                });
                await this.getARTemplates();
            }
            this.loading = false;
        },
        handleRemove(item) {
            this.showModal = true;
            this.modalParams = {
                title: this.$t('analysisRequests.labels.template.deleteTitle'),
                component: Vue.component('ConfirmForm', ConfirmForm),
                payload: {
                    msg: this.$t('analysisRequests.labels.template.confirmRemove'),
                },
                onConfirm: () => this.deleteTemplate(item),
                isForm: false,
            };
        },
        handleEdit(item) {
            this.sample = {
                ...item,
                site: this.site.id,
                client: this.client.id,
                component: item.componentId,
            };
            this.isEdition = true;
            this.isVisible = true;
        },
        async duplicate(item) {
            this.loading = true;
            const { data } = await this.$rq.duplicateAnalysisRequestsTemplates(this.site.id, item.id);
            if (data) {
                this.$toasted.global.toast_success({
                    message: this.$t('analysisRequests.templateEdited'),
                });
                await this.getARTemplates();
            }
            this.loading = false;
        },
        async start(item) {
            this.loading = true;
            const { data } = await this.$rq.startAnalysisRequestsTemplates(this.site.id, item.id);
            this.loading = false;
            return data;
        },
        handleDuplicate(item) {
            this.showModal = true;
            this.modalParams = {
                title: this.$t('analysisRequests.labels.template.duplicateTitle'),
                component: Vue.component('ConfirmForm', ConfirmForm),
                payload: {
                    msg: this.$t('analysisRequests.labels.template.confirmDuplicate'),
                },
                onConfirm: () => this.duplicate(item),
                isForm: false,
            };
        },
        handleStart(item) {
            this.showModal = true;
            this.modalParams = {
                title: this.$t('analysisRequests.labels.template.startTitle'),
                component: Vue.component('ConfirmForm', ConfirmForm),
                payload: {
                    msg: this.$t('analysisRequests.labels.template.confirmStart'),
                },
                onConfirm: async () => {
                    const { data } = await this.start(item);
                    if (data) {
                        this.showModal = false;
                        this.isVisible = false;
                        this.$root.$emit('destroyModal');
                        this.$router.push({
                            name: 'analysisRequests',
                        });
                    }
                },
                isForm: false,
            };
        },
        onClickAction(item, action) {
            if (item && item === 'add') {
                // header action
                this.sample = {
                    site: this.site.id,
                    client: this.client.id,
                };
                this.isEdition = false;
                this.isVisible = true;
            } else {
                // item action
                switch (action.icon) {
                    case 'play_arrow':
                        this.handleStart(item);
                        break;
                    case 'content_copy':
                        this.handleDuplicate(item);
                        break;
                    case 'edit':
                        this.handleEdit(item);
                        break;
                    case 'delete':
                        this.handleRemove(item);
                        break;
                    default:
                        break;
                }
            }
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getARTemplates();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
    },
};
</script>

  <style lang="scss" scoped>
    .analysisRequest-action-bar {
        position: sticky;
        top: 64px;
        background: #fff;
        z-index: 8;
        box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.25);
    }
    .linkWO {
      text-align: center;
    }
    .receivedDate {
      margin-bottom: 13px;
    }
  </style>
